import React from "react";
import { LetterTile } from "@/components/LetterTile";

const LETTER_VALUES = {
  A: 1, E: 1, I: 1, L: 1, N: 1, O: 1, R: 1, S: 1, T: 1, U: 1,
  D: 2, G: 2, M: 2, B: 3, C: 3, P: 3, F: 4, H: 4, V: 4,
  J: 8, Q: 8, K: 10, W: 10, X: 10, Y: 10, Z: 10, "?": 0,
};

/**
 * GameBoard – Globo official plateau (9 cols × 6 rows).
 * Top 3 rows are upright (player 1 perspective), bottom 3 rows are
 * visually rotated 180° to reproduce the printed plateau.
 * Each cell shows the engraved plateau letter (from `plateau`) as a visual guide.
 * When a tile is placed, the LetterTile renders on top of the engraving.
 */
export function GameBoard({
  board = [],
  bonuses = [],
  plateau = [],
  placed = [],
  onCellClick,
  size = 56,
  center = [2, 4],
  rotatedForPlayer = false,
}) {
  const rows = board.length;
  const cols = rows ? board[0].length : 0;
  const isPlaced = (r, c) => placed.find((p) => p.row === r && p.col === c);
  const [centerR, centerC] = center;

  // "Zone d'Émergence" — the highlighted circle around the center
  const isInEmergenceZone = (r, c) => {
    const dr = Math.abs(r - (rows - 1) / 2);
    const dc = Math.abs(c - centerC);
    return dr <= 1 && dc <= 2;
  };

  return (
    <div
      className={`plateau-frame inline-block p-3 rounded-2xl relative ${
        rotatedForPlayer ? "rotate-180" : ""
      }`}
      data-testid="game-board"
    >
      <div
        className="grid gap-1 relative"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {board.map((row, r) =>
          row.map((cell, c) => {
            const bonus = bonuses[r]?.[c] || "";
            const engraved = plateau[r]?.[c];
            const isMirrored = r >= rows / 2;
            const placedT = isPlaced(r, c);
            const letter = cell || placedT?.letter;
            const isCenter = r === centerR && c === centerC;
            const inZone = isInEmergenceZone(r, c);

            return (
              <button
                type="button"
                key={`${r}-${c}`}
                className={`plateau-cell rounded-sm ${bonus ? `bonus-${bonus}` : ""} ${
                  isCenter ? "center" : ""
                } ${inZone ? "in-emergence" : ""} flex items-center justify-center relative`}
                style={{ width: size, height: size }}
                onClick={() => onCellClick && onCellClick(r, c)}
                data-testid={`board-cell-${r}-${c}`}
              >
                {/* Engraved plateau letter (visual bg) */}
                {engraved && engraved !== "." && (
                  <span
                    className={`absolute inset-0 flex items-center justify-center pointer-events-none plateau-engrave ${
                      isMirrored ? "rotate-180" : ""
                    }`}
                  >
                    <span className="text-2xl font-display font-black opacity-60 select-none">
                      {engraved}
                    </span>
                    {r === 0 && (
                      <span className="absolute top-0.5 right-1 text-[8px] font-semibold opacity-70">
                        {c + 1}
                      </span>
                    )}
                    {r === rows - 1 && (
                      <span className="absolute bottom-0.5 left-1 text-[8px] font-semibold opacity-70 rotate-180">
                        {cols - c}
                      </span>
                    )}
                  </span>
                )}
                {isCenter && !letter && (
                  <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="w-2 h-2 rounded-full bg-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.2)]" />
                  </span>
                )}
                {letter && (
                  <div className="absolute inset-0.5 z-10">
                    <LetterTile
                      letter={letter}
                      points={LETTER_VALUES[letter]}
                      size="sm"
                      onClick={onCellClick ? () => onCellClick(r, c) : undefined}
                      testId={`board-tile-${r}-${c}`}
                    />
                  </div>
                )}
                {bonus && !letter && !engraved && (
                  <span className="text-[9px] font-bold text-muted-foreground opacity-70">{bonus}</span>
                )}
              </button>
            );
          })
        )}
      </div>
      {/* Zone d'Émergence — decorative circle overlay */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div
          className="rounded-full ring-2 ring-primary/40"
          style={{ width: size * 5, height: size * 3.2 }}
        />
      </div>
    </div>
  );
}
